import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { auth, db } from '../../firebase';
import { fetchUserProfile } from './actions/authActions';
import { initializeDashboard, loadVersion, loadDashboardById } from '../../components/containers/Dashboard/actions/dashboardActions';
import Login from '../../dummies/LoginDialog/LoginDialog';
import OnboardingWizardFlow from '../OnboardingWizardFlow/OnboardingWizardFlow';
import googleOneTapService from '../../services/googleOneTapService';
import LoadingOverlay from '../../dummies/LoadingOverlay/LoadingOverlay';
import UpgradeScreen from '../../dummies/UpgradeScreen/UpgradeScreen';
import { selectIsLicenseExpired } from '../../store/actions/licenseActions';
import { masqueradeUtils } from '../../utils/masqueradeUtils';

const AuthManager = ({
  user,
  authLoading,
  authError,
  dashboardLoading,
  dashboardError,
  dashboard,
  dispatch,
  children,
  catalog = null,
  organization = null,
  predefinedTemplates = [],
}) => {
  const { organizationId, dashboardId, screenId } = useParams();
  const navigate = useNavigate();
  const currentVersionIdRef = useRef(null);
  const loadedDashboardIdRef = useRef(null);
  const isLicenseExpired = useSelector(selectIsLicenseExpired);

  // Masquerade: activate on first load with ?masquerade=true, then persist via sessionStorage.
  // sessionStorage is per-tab and can only be set from the admin panel (which already verified admin status),
  // so we don't need to re-check isAdmin here — that would cause a race condition since admin UIDs
  // load async after the user profile, and the redirect fires before they arrive.
  useEffect(() => {
    if (window.location.search.includes('masquerade=true') && organizationId) {
      masqueradeUtils.activate(organizationId);
      const url = new URL(window.location);
      url.searchParams.delete('masquerade');
      window.history.replaceState({}, '', url.pathname);
    }
  }, [organizationId]);

  const isMasquerade = masqueradeUtils.isActive();

  // Listen for Firebase Auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser && !user) {
        dispatch(fetchUserProfile(firebaseUser));
      }
    });
    return () => unsubscribe();
  }, [dispatch, user]);

  // אתחול Google One Tap כשהקומפוננטה נטענת
  useEffect(() => {
    // רק אם אין משתמש מחובר ואין dashboardId (לא במצב צפייה ציבורית), נציג את Google One Tap
    if (!user && !authLoading && !dashboardId) {
      // המתן קצת כדי לוודא שהדף נטען לגמרי
      const timer = setTimeout(() => {
        googleOneTapService.initialize();
        googleOneTapService.prompt();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [user, authLoading, dashboardId]);

  // Once we have a user with organizationId, initialize the dashboard
  useEffect(() => {
    // If there's a dashboardId in URL, don't load by organization - load specific dashboard instead
    if (dashboardId) {
      return;
    }

    // In masquerade mode, Screen.jsx handles data loading via URL params
    if (isMasquerade) {
      return;
    }

    if (user && user.organizationId && !dashboard && !dashboardLoading && !dashboardError) {
      dispatch(initializeDashboard(user));
    }
  }, [user, dashboard, dashboardLoading, dashboardError, dashboardId, isMasquerade, dispatch]);

  // If there's a dashboardId in URL, load that specific dashboard
  useEffect(() => {
    if (dashboardId && !dashboardLoading && !dashboardError) {
      // אם כבר טענו את הדשבורד הזה, אל תטען שוב
      if (loadedDashboardIdRef.current === dashboardId) {
        return;
      }

      // טען את הדשבורד רק אם:
      // 1. אין dashboard כלל, או
      // 2. ה-dashboard הנוכחי הוא לא הדשבורד שאנחנו רוצים
      const currentDashboardId = dashboard?.dashboardId;

      if (!dashboard || currentDashboardId !== dashboardId) {
        loadedDashboardIdRef.current = dashboardId;
        dispatch(loadDashboardById(dashboardId));
      } else {
        loadedDashboardIdRef.current = dashboardId;
      }
    }

    // אם אין dashboardId בURL, אפס את ה-ref
    if (!dashboardId) {
      loadedDashboardIdRef.current = null;
    }
  }, [dashboardId, dashboard, dashboardLoading, dashboardError, dispatch]);

  // Realtime listener for version updates when viewing public dashboard
  useEffect(() => {
    if (!dashboardId) {
      return;
    }

    if (!dashboard?.dashboardId) {
      return;
    }

    const dashboardRef = doc(db, 'dashboards', dashboard.dashboardId);

    const unsubscribe = onSnapshot(dashboardRef, (docSnap) => {
      if (!docSnap.exists()) {
        return;
      }

      const data = docSnap.data();
      // תמיכה בשתי הסכימות: latestVersionId (חדש) או מערך versions (ישן)
      const currentVersionId = data.latestVersionId
        || data.versions?.[data.versions.length - 1]?.versionId;

      if (!currentVersionId) {
        return;
      }

      // אם זו הפעם הראשונה, פשוט שמור את ה-versionId
      if (!currentVersionIdRef.current) {
        currentVersionIdRef.current = currentVersionId;
        return;
      }

      // בודק אם יש גירסה חדשה
      if (currentVersionId !== currentVersionIdRef.current) {
        const isPublicView = !user;
        dispatch(loadVersion(dashboard.dashboardId, currentVersionId, isPublicView));
        currentVersionIdRef.current = currentVersionId;
      }
    }, (error) => {
      console.error('❌ Error in realtime listener:', error);
    });

    return () => {
      unsubscribe();
    };
  }, [dashboardId, dashboard?.dashboardId, user, dispatch]);

  useEffect(() => {
    // In masquerade mode, never redirect — let the URL org stay
    if (isMasquerade && organizationId) {
      return;
    }

    if (user && user.organizationId) {
      // אם יש dashboardId בURL, אל תנווט - השאר את המשתמש באותו מקום
      if (dashboardId) {
        return; // אל תעשה כלום, השאר את המשתמש בדשבורד הספציפי
      }

      // אם יש screenId בURL, אל תנווט - השאר את המשתמש באותו מקום
      if (screenId) {
        return;
      }

      // רק אם אין dashboardId ואין screenId ואנחנו לא בURL הנכון, נווט לדף הראשי
      if (!organizationId || organizationId !== user.organizationId) {
        navigate(`/${user.organizationId}/screens`, { replace: true });
      }
    }
  }, [user, organizationId, dashboardId, screenId, navigate, isMasquerade]);

  // Handler for login
  const handleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error during signInWithPopup:", error);
    }
  };

  // Render Logic
  const isNewUser = !user || !user.organizationId;
  const isPublicView = !!dashboardId; // מצב צפייה ציבורית אם יש dashboardId
  const isLoading = authLoading || (user && user.organizationId && dashboardLoading) || (isPublicView && dashboardLoading);
  const error = authError || dashboardError;

  if (isLoading) {
    return <LoadingOverlay open={true} />;
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f5f7fa',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Box
          sx={{
            color: '#dc2626',
            fontSize: '1.25rem',
            fontWeight: 600
          }}
        >
          שגיאה
        </Box>
        <Box
          sx={{
            color: '#64748b',
            fontSize: '0.875rem'
          }}
        >
          {error}
        </Box>
      </Box>
    );
  }

  // אם אין משתמש ואין dashboardId (לא במצב צפייה ציבורית), הצג מסך התחברות
  if (!user && !isPublicView && !isMasquerade) {
    return <Login onLogin={handleLogin} error={authError} />;
  }

  // אם הרישיון פג — הצג מסך שדרוג (לא במצב צפייה ציבורית)
  if (user && user.organizationId && !isPublicView && !isMasquerade && isLicenseExpired) {
    return <UpgradeScreen />;
  }

  // אם משתמש חדש (אין organizationId) ולא במצב צפייה ציבורית, הצג ויזרד הכנסה
  if (isNewUser && !isPublicView && !isMasquerade) {
    return (
      <OnboardingWizardFlow
        open={true}
        user={user}
        organization={organization}
        catalog={catalog}
        predefinedTemplates={predefinedTemplates}
        dispatch={dispatch}
        navigate={navigate}
      />
    );
  }

  // אם יש children - תמיד הצג אותם (App.js מחליט מה להציג)
  if (children) {
    if (typeof children === 'function') {
      return children({ organizationId, dashboardId, screenId });
    }
    return children;
  }

  return <LoadingOverlay open={true} />;
};

export default AuthManager;
